/**
 * admin/capabilities — framework-agnostic RBAC role → capability map.
 *
 * Mirrors the backend capability gating (campaign #18, Track B). The back-office
 * UI uses this to (a) decide whether a user may enter the admin area at all and
 * (b) hide/disable individual actions a role can't perform. The backend remains
 * the source of truth and re-checks every mutation; this is purely a UX layer so
 * the client never offers an action that would 403.
 *
 * The model has to live in TWO runtimes (the server decides, while the UI hides
 * what the server will reject), so there is no way to have a single
 * implementation — but there is a single CONTRACT: `rbac-contract.json`. The
 * runtime ranges derive from it, and tests on both sides assert that their maps
 * match it. Previously each side only checked itself, which is exactly how the
 * backend became more permissive than the UI (a single `ManageUsers` for both
 * banning AND assigning roles) without anything turning red.
 *
 * Roles (broad → narrow):
 *   - super_admin — everything, including destructive GDPR removals + role grants
 *   - admin       — manage users/flags/content, but not destructive removals
 *   - support     — read users + audit, ban/unban + revoke sessions + content
 *                   (no role changes, no flag writes, no removals)
 *   - read_only   — read-only access to every admin panel
 *   - user        — no admin access
 */

import contract from "./rbac-contract.json";

export const ADMIN_ROLES = ["super_admin", "admin", "support", "read_only", "user"] as const;

export type AdminRole = (typeof ADMIN_ROLES)[number];

/** Discrete capabilities the back-office UI gates on. */
export type AdminCapability =
  | "admin.access"
  | "users.read"
  | "users.setRole"
  | "users.ban"
  | "users.revokeSessions"
  | "users.remove"
  | "audit.read"
  | "integrations.read"
  | "integrations.testEmail"
  | "flags.read"
  | "flags.write"
  | "content.write"
  | "metrics.read";

const READ_CAPS: AdminCapability[] = [
  "admin.access",
  "users.read",
  "audit.read",
  "integrations.read",
  "flags.read",
  "metrics.read",
];

const SUPPORT_CAPS: AdminCapability[] = [
  ...READ_CAPS,
  "users.ban",
  "users.revokeSessions",
  "integrations.testEmail",
  // Mirror of `Capability::ManageContent`: themes, fonts, templates, and
  // email campaigns. It was missing, so the UI could not even express
  // the restriction and rendered those buttons for `read_only`.
  "content.write",
];

const ADMIN_CAPS: AdminCapability[] = [...SUPPORT_CAPS, "users.setRole", "flags.write"];

const SUPER_ADMIN_CAPS: AdminCapability[] = [...ADMIN_CAPS, "users.remove"];

const CAPABILITIES_BY_ROLE: Record<AdminRole, AdminCapability[]> = {
  super_admin: SUPER_ADMIN_CAPS,
  admin: ADMIN_CAPS,
  support: SUPPORT_CAPS,
  read_only: READ_CAPS,
  user: [],
};

/**
 * Hierarchical role order, DERIVED from the runtime contract.
 * It is written key by key (rather than with `Object.fromEntries(...) as Record<…>`)
 * so the compiler checks that the contract contains all five roles, instead of
 * a type assertion fabricating that without looking.
 */
export const ROLE_RANK: Record<AdminRole, number> = {
  super_admin: contract.roles.super_admin.rank,
  admin: contract.roles.admin.rank,
  support: contract.roles.support.rank,
  read_only: contract.roles.read_only.rank,
  user: contract.roles.user.rank,
};

function isAdminRole(role: string | null | undefined): role is AdminRole {
  return typeof role === "string" && (ADMIN_ROLES as readonly string[]).includes(role);
}

/** Narrows an arbitrary string (e.g. `session.user.role`) to a known role. */
export function toAdminRole(role: string | null | undefined): AdminRole {
  return isAdminRole(role) ? role : "user";
}

/** Returns the capability set for a role string (unknown → `user` → none). */
export function capabilitiesForRole(role: string | null | undefined): ReadonlySet<AdminCapability> {
  return new Set(CAPABILITIES_BY_ROLE[toAdminRole(role)]);
}

/** True when the role grants the given capability. */
export function roleCan(role: string | null | undefined, capability: AdminCapability): boolean {
  return capabilitiesForRole(role).has(capability);
}

/** True when the role may enter the admin back-office at all. */
export function isAdminCapable(role: string | null | undefined): boolean {
  return roleCan(role, "admin.access");
}

/**
 * Roles that `actorRole` can GRANT — never above their own.
 *
 * Replaces the old `ASSIGNABLE_ROLES = ADMIN_ROLES` constant (the entire
 * list, for anyone): the name did not require saying *assignable by whom*,
 * while this function's signature does. Mirror of `can_grant_role` in Rust.
 */
export function assignableRolesFor(actorRole: string | null | undefined): readonly AdminRole[] {
  if (!roleCan(actorRole, "users.setRole")) return [];
  const rank = ROLE_RANK[toAdminRole(actorRole)];
  return ADMIN_ROLES.filter((r) => ROLE_RANK[r] <= rank);
}

/**
 * Can the actor administer (ban, revoke, reset MFA, change the role of)
 * a user whose CURRENT role is `targetRole`? Strictly higher rank, with
 * `super_admin` allowed over peers — otherwise, a dishonest `super_admin`
 * would be untouchable. Mirror of `can_administer` in Rust.
 */
export function canAdministerUser(
  actorRole: string | null | undefined,
  targetRole: string | null | undefined,
): boolean {
  const actor = toAdminRole(actorRole);
  if (actor === "super_admin") return true;
  return ROLE_RANK[toAdminRole(targetRole)] < ROLE_RANK[actor];
}
